import React, { JSX } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";

// Helpers
import { currentBalanceDate } from "sj-ui-toolkit/src/utils/dates";
import { withCurrency } from "@/helpers/reports";

/**
 * Represents the current license information for per-user billing mode
 */
interface CurrentLicense {
  /** Total number of purchased licenses */
  userCount?: number;
  /** Current number of active users */
  currentUserCount?: number;
  /** Current number of active administrators */
  currentAdminsCount?: number;
  /** Number of archived users */
  currentArchivedCount?: number;
  /** Number of unlicensed users */
  currentUnlicensedCount?: number;
  /** License expiration date (ISO string) */
  expiration?: string;
}

/**
 * Billing mode types supported by the component
 */
type BillingTypeMode = "per_lesson" | "per_user" | null;

interface AccountBalanceProps {
  /** Current account balance (used in per_lesson mode) */
  accountBalance?: number;
  /** Price per lesson */
  lessonPrice?: number;
  /** Price for dojo activities (Champion Passport) */
  dojoActivityPrice?: number;
  /** Price for awareness lessons */
  awarenessLessonPrice?: number;
  /** Price for coding exercises (experiments) */
  experimentPrice?: number;
  /** Current billing mode type */
  billingTypeMode?: BillingTypeMode;
  /** Current license information (used in per_user mode) */
  currentLicense?: CurrentLicense;
  /** Whether the component is in loading state */
  isLoading?: boolean;
}

// ============================================
// Component
// ============================================

const AccountBalance: React.FC<AccountBalanceProps> = ({
  accountBalance,
  lessonPrice,
  dojoActivityPrice,
  awarenessLessonPrice,
  experimentPrice,
  billingTypeMode,
  currentLicense,
  isLoading = false,
}) => {
  const skeletonData = Array(4)
    .fill(null)
    .map(() => ({ label: null, value: null }));

  const renderContent = (): JSX.Element | null => {
    if (isLoading) {
      return (
        <div className="mb-4">
          <Skeleton className="h-6 mb-2 max-w-48" />
          <DataTable
            value={skeletonData}
            className="p-datatable-sm"
            tableClassName="dt-responsive-text"
            aria-label="License balance"
            emptyMessage="No license data available."
          >
            <Column field="label" header="Details" body={<Skeleton className="min-h-6" />} />
            <Column field="value" header="Value" body={<Skeleton className="min-h-6" />} />
          </DataTable>
        </div>
      );
    }

    if (billingTypeMode === "per_lesson") {
      return (
        <div className="mb-4">
          <p className="body-lg font-semibold mb-2">
            Current Balance: {withCurrency(accountBalance || 0)}
          </p>
          <DataTable
            value={[
              { type: "Awareness Lesson Price", price: awarenessLessonPrice },
              { type: "Champion Passport Price", price: dojoActivityPrice },
              { type: "Lesson Price", price: lessonPrice },
              { type: "Coding Exercise Price", price: experimentPrice },
            ]}
            className="p-datatable-sm"
            tableClassName="dt-responsive-text"
            aria-label="Transaction history"
            emptyMessage="No transaction history."
          >
            <Column field="type" header="Billing Type" />
            <Column
              field="price"
              header="Per Lesson"
              body={rowData => withCurrency(rowData.price ?? 0)}
            />
          </DataTable>
        </div>
      );
    } else if (billingTypeMode === "per_user" && currentLicense) {
      const archivedUsers =
        (currentLicense.currentArchivedCount || 0) + (currentLicense.currentUnlicensedCount || 0);

      return (
        <div className="mb-4">
          <DataTable
            size="large"
            tableClassName="dt-responsive-text"
            value={[
              {
                label: "Purchased Licenses",
                value: currentLicense.userCount ?? currentLicense.currentUserCount,
              },
              { label: "Active Learners", value: currentLicense.currentUserCount },
              { label: "Active Administrators", value: currentLicense.currentAdminsCount },
              { label: "Archived Users", value: archivedUsers },
              { label: "Expiration Date", value: currentBalanceDate(currentLicense.expiration) },
            ]}
            className="p-datatable-sm"
            aria-label="Account Overview"
            emptyMessage="No usage details."
          >
            <Column field="label" header="License Details" />
            <Column field="value" header="Count" />
          </DataTable>
        </div>
      );
    }

    return null;
  };

  return (
    <Card>
      <h2 className="mb-4">Account Overview</h2>
      {renderContent()}
    </Card>
  );
};

export default AccountBalance;
